import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { axiosInstance } from '../lib/api_client'
import { ModalWrapper } from './modal_wrapper'

const downloadCsv = async (path: string, filename: string) => {
  const response = await axiosInstance.get<string>(path, {
    responseType: 'text',
    transformResponse: [(data: string) => data],
  })
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

const serverError = (error: unknown): string | undefined => {
  const errors = (error as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors
  return Array.isArray(errors) && errors.length > 0 ? errors.join('\n') : undefined
}

const validMarkup = (percent: string) => /^\d{1,3}$/.test(percent.trim()) && Number(percent) <= 100

type CredentialsProps = {
  busy: boolean
  setBusy: (value: boolean) => void
  percent: string
}

const Credentials = ({ busy, setBusy, percent }: CredentialsProps) => {
  const { t } = useTranslation()
  const [shopId, setShopId] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [publishStatus, setPublishStatus] = useState('')
  const [smsAddress, setSmsAddress] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    axiosInstance.get<{
      shopId?: string
      email?: string
      publishStatus?: number | null
      smsAddress?: string
    }>('/mercari_shops/credentials')
      .then((response) => {
        setShopId(response.data.shopId || '')
        setEmail(response.data.email || '')
        setPublishStatus(response.data.publishStatus == null ? '' : String(response.data.publishStatus))
        setSmsAddress(response.data.smsAddress || '')
      })
      .catch(() => undefined)
  }, [])

  const save = async () => {
    setSaved(false)
    if (!validMarkup(percent)) {
      alert(t('account.sell.mercari.markup_invalid'))
      return
    }
    setBusy(true)
    try {
      await axiosInstance.patch('/mercari_shops/markup', { percent: Number(percent) })
      await axiosInstance.patch('/mercari_shops/credentials', {
        shopId,
        email,
        password,
        publishStatus: publishStatus === '' ? '' : Number(publishStatus),
      })
      setPassword('')
      setSaved(true)
    } catch (error) {
      alert(serverError(error) ?? t('account.sell.mercari.save_failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      { smsAddress && <p>{ t('account.sell.mercari.sms_address', { address: smsAddress }) }</p> }
      <div className="mercari-settings-fields">
        <label htmlFor="mercari-shop-id">{ t('account.sell.mercari.shop_id') }</label>
        <input id="mercari-shop-id" type="text" className="full" value={ shopId } disabled={ busy } onChange={ (event) => setShopId(event.target.value) } />
        <label htmlFor="mercari-email">{ t('account.sell.mercari.email') }</label>
        <input id="mercari-email" type="email" className="full" value={ email } disabled={ busy } onChange={ (event) => setEmail(event.target.value) } />
        <label htmlFor="mercari-password">{ t('account.sell.mercari.password') }</label>
        <input id="mercari-password" type="password" className="full" value={ password } disabled={ busy } onChange={ (event) => setPassword(event.target.value) } autoComplete="new-password" />
        <label htmlFor="mercari-publish-status">{ t('account.sell.mercari.publish_status') }</label>
        <select id="mercari-publish-status" className="full" value={ publishStatus } disabled={ busy } onChange={ (event) => setPublishStatus(event.target.value) }>
          <option value="">{ t('account.sell.mercari.publish_inherit') }</option>
          <option value="1">{ t('account.sell.mercari.publish_private') }</option>
          <option value="2">{ t('account.sell.mercari.publish_public') }</option>
        </select>
      </div>
      <p>{ t('account.sell.mercari.publish_help') }</p>
      <p>
        <button type="button" className="button full" disabled={ busy } onClick={ () => { void save() } }>
          { t('account.sell.mercari.save_credentials') }
        </button>
      </p>
      { saved && <p>{ t('account.sell.mercari.credentials_saved') }</p> }
    </>
  )
}

const MercariShopsDesk = () => {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [percent, setPercent] = useState('10')
  const [settingsOpen, setSettingsOpen] = useState(false)

  useEffect(() => {
    axiosInstance.get<{ percent: number }>('/mercari_shops/markup')
      .then((response) => setPercent(String(response.data.percent)))
      .catch(() => undefined)
  }, [])

  const run = (task: () => Promise<void>) => {
    setBusy(true)
    task().catch(() => alert(t('account.sell.mercari.download_failed'))).finally(() => setBusy(false))
  }

  const savePercent = async () => {
    const value = Number(percent)
    if (!validMarkup(percent) || !Number.isInteger(value)) throw new Error('markup')
    await axiosInstance.patch('/mercari_shops/markup', { percent: value })
  }

  const download = (path: string, filename: string) => () => run(async () => {
    await savePercent()
    await downloadCsv(path, filename)
  })

  const recordIds = async (file: File) => {
    const body = new FormData()
    body.append('file', file)
    const response = await axiosInstance.post<{ updated: number, warnings: number }>('/mercari_shops/product_ids', body)
    const recorded = t('account.sell.mercari.recorded', { count: response.data.updated })
    const warnings = response.data.warnings
    alert(warnings > 0 ? `${recorded}\n${t('account.sell.mercari.duplicate_codes', { count: warnings })}` : recorded)
  }

  return (
    <>
      <hr />
      <h2>{ t('account.sell.mercari.title') }</h2>
      <p>{ t('account.sell.mercari.intro') }</p>
      <p>{ t('account.sell.mercari.fallback') }</p>
      <p>{ t('account.sell.mercari.defaults') }</p>
      <p>
        <button type="button" className="button" onClick={ () => setSettingsOpen(true) }>
          { t('account.sell.mercari.settings') }
        </button>
      </p>
      { settingsOpen && (
        <ModalWrapper>
          <div className="information">
            <h1>{ t('account.sell.mercari.settings') }</h1>
            <div className="mercari-settings-fields">
              <label htmlFor="mercari-markup">{ t('account.sell.mercari.markup') }</label>
              <input
                id="mercari-markup"
                type="number"
                className="full"
                min={ 0 }
                max={ 100 }
                step={ 1 }
                value={ percent }
                disabled={ busy }
                onChange={ (event) => setPercent(event.target.value) }
              />
            </div>
            <h3>{ t('account.sell.mercari.credentials') }</h3>
            <Credentials busy={ busy } setBusy={ setBusy } percent={ percent } />
            <p>
              <button type="button" className="full" onClick={ () => setSettingsOpen(false) }>
                { t('account.sell.mercari.close') }
              </button>
            </p>
          </div>
        </ModalWrapper>
      ) }
      <h3>{ t('account.sell.mercari.manual') }</h3>
      <p>
        <button type="button" className="button" disabled={ busy } onClick={ download('/mercari_shops/create_csv', 'mercari_shops_create.csv') }>
          { t('account.sell.mercari.download_create') }
        </button>
        {' '}
        <button type="button" className="button" disabled={ busy } onClick={ download('/mercari_shops/update_csv', 'mercari_shops_update.csv') }>
          { t('account.sell.mercari.download_update') }
        </button>
      </p>
      <p>{ t('account.sell.mercari.record_help') }</p>
      <p>
        <input
          type="file"
          accept=".csv,text/csv"
          disabled={ busy }
          onChange={ (event) => {
            const file = event.target.files?.[0]
            event.target.value = ''
            if (file) run(() => recordIds(file))
          } }
        />
      </p>
    </>
  )
}

export { MercariShopsDesk }
